(() => {
  const NativeDrawer = customElements.get('drawer-component');

  class StandaloneDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
      this.onControlClick = this.onControlClick.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
    }

    connectedCallback() {
      this.controls = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
      this.controls.forEach((control) => control.addEventListener('click', this.onControlClick));
      document.addEventListener('keyup', this.onKeyUp);
    }

    disconnectedCallback() {
      this.controls?.forEach((control) => control.removeEventListener('click', this.onControlClick));
      document.removeEventListener('keyup', this.onKeyUp);
    }

    get open() {
      return this.hasAttribute('open');
    }

    onControlClick(event) {
      event.preventDefault();
      this.open ? this.hide() : this.show(event.currentTarget);
    }

    onKeyUp(event) {
      if (event.code === 'Escape' && this.open) this.hide();
    }

    show(focusElement = null, animate = true) {
      if (this.open) return;
      this.activeElement = focusElement || document.activeElement;
      this.hidden = false;
      this.removeAttribute('inert');
      this.setAttribute('open', '');
      document.body.classList.add('modal-showing');

      const activate = () => {
        this.setAttribute('active', '');
        document.body.classList.add('modal-show');
        document.body.classList.remove('modal-showing');
      };

      animate ? window.requestAnimationFrame(() => window.requestAnimationFrame(activate)) : activate();
    }

    hide(animate = true) {
      if (!this.open) return;
      this.removeAttribute('active');
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      document.body.classList.remove('modal-show', 'modal-showing');

      const finish = () => {
        if (!this.open) this.hidden = true;
        this.activeElement?.focus?.();
      };

      if (!animate) {
        finish();
        return;
      }

      let finished = false;
      const onFinish = () => {
        if (finished) return;
        finished = true;
        this.removeEventListener('transitionend', onFinish);
        finish();
      };
      this.addEventListener('transitionend', onFinish, { once: true });
      window.setTimeout(onFinish, 350);
    }
  }

  const CartDrawerBase = NativeDrawer || StandaloneDrawer;

  const money = (cents) => {
    if (window.FoxTheme?.Currency?.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(cents / 100);
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const imageUrl = (url) => url ? `${url}${url.includes('?') ? '&' : '?'}width=200` : '';

  class SourceCartDrawer extends CartDrawerBase {
    constructor() {
      super();
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.onCartRefresh = this.onCartRefresh.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', this.onDrawerClick);
      this.addEventListener('change', this.onQuantityChange);
      document.addEventListener('spx:cart-added', this.onProductAdded);
      document.addEventListener('cart:refresh', this.onCartRefresh);
      this.viewport = window.visualViewport;
      this.viewport?.addEventListener('resize', this.onViewportChange);
      this.viewport?.addEventListener('scroll', this.onViewportChange);
      this.onViewportChange();
      this.unsubscribe = window.FoxTheme?.pubsub?.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate
      );
      this.refreshCart();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('click', this.onDrawerClick);
      this.removeEventListener('change', this.onQuantityChange);
      document.removeEventListener('spx:cart-added', this.onProductAdded);
      document.removeEventListener('cart:refresh', this.onCartRefresh);
      this.viewport?.removeEventListener('resize', this.onViewportChange);
      this.viewport?.removeEventListener('scroll', this.onViewportChange);
      window.cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = null;
      this.style.removeProperty('--source-cart-drawer-viewport-height');
      this.removeAttribute('data-source-cart-compact-height');
      this.unsubscribe?.();
    }

    get requiresBodyAppended() { return false; }

    onViewportChange() {
      if (this.viewportFrame) return;

      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null;
        const viewportHeight = window.visualViewport?.height;
        if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return;

        this.style.setProperty('--source-cart-drawer-viewport-height', `${Math.round(viewportHeight)}px`);
        const isLandscape = window.matchMedia?.('(orientation: landscape)').matches;
        this.toggleAttribute('data-source-cart-compact-height', viewportHeight <= 440 || (isLandscape && viewportHeight <= 520));
      });
    }

    show(focusElement = null, animate = true) {
      this.refreshCart();
      return super.show(focusElement, animate);
    }

    async refreshCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        this.renderCart(await response.json());
      } catch (error) {
        console.error('Unable to refresh cart drawer', error);
      }
    }

    onCartUpdate(event) {
      if (event?.cart && !event.cart.errors) this.renderCart(event.cart);
    }

    onProductAdded(event) {
      const { cart, source } = event.detail || {};
      if (!cart || cart.errors) return;
      this.renderCart(cart);
      this.show(source || null);
    }

    onCartRefresh() {
      this.refreshCart();
    }

    async changeLine(line, quantity) {
      this.classList.add('is-loading');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || 'Cart update failed');
        this.renderCart(cart);
        window.FoxTheme?.pubsub?.publish?.(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      } catch (error) {
        console.error('Unable to update cart line', error);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    onDrawerClick(event) {
      const removeButton = event.target.closest('[data-source-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.changeLine(Number(removeButton.dataset.sourceCartRemove), 0);
        return;
      }

      const quantityButton = event.target.closest('.cart-quantity .quantity__button');
      if (quantityButton) {
        event.preventDefault();
        const quantity = quantityButton.closest('quantity-input');
        const input = quantity?.querySelector('[data-source-cart-quantity]');
        if (!input) return;
        const delta = quantityButton.name === 'minus' ? -1 : 1;
        const nextQuantity = Math.max(0, Number(input.value || 0) + delta);
        this.changeLine(Number(input.dataset.sourceCartQuantity), nextQuantity);
      }
    }

    onQuantityChange(event) {
      const input = event.target.closest('[data-source-cart-quantity]');
      if (input) this.changeLine(Number(input.dataset.sourceCartQuantity), Math.max(0, Number(input.value || 0)));
    }

    renderCart(cart) {
      this.cartLoaded = true;
      const isEmpty = cart.item_count === 0;
      this.querySelector('[data-source-cart-empty]')?.classList.toggle('hidden', !isEmpty);
      this.querySelector('[data-source-cart-body]')?.classList.toggle('hidden', isEmpty);
      this.querySelector('[data-source-cart-footer]')?.classList.toggle('hidden', isEmpty);

      const list = this.querySelector('[data-source-cart-items]');
      if (list) list.innerHTML = cart.items.map((item, index) => this.itemMarkup(item, index + 1)).join('');

      const total = this.querySelector('[data-source-cart-total]');
      if (total) total.textContent = money(cart.total_price);

      this.renderShipping(cart.total_price);
      this.renderPromotion(cart.item_count);
      this.renderDiscounts(cart.cart_level_discount_applications || []);
      this.applySwedishText(cart.item_count, cart.total_price);
      document.querySelectorAll('cart-count').forEach((count) => {
        const isDrawerHeading = count.classList.contains('cart-count--drawer-heading') || count.dataset.type === 'blank';
        count.textContent = isDrawerHeading ? `(${cart.item_count})` : cart.item_count;
        count.classList.toggle('cart-count--blank', isDrawerHeading || isEmpty);
        count.hidden = isDrawerHeading ? false : isEmpty;
        count.setAttribute('aria-label', `${cart.item_count} Artikel`);
      });
      document.documentElement.classList.add('cart-count-ready');
      document.documentElement.classList.toggle('cart-has-items', !isEmpty);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    applySwedishText(itemCount = 0, cartTotal = null) {
      const decode = (value) => {
        const node = document.createElement('textarea');
        node.innerHTML = value;
        return node.value;
      };
      const setText = (selector, value) => {
        this.querySelectorAll(selector).forEach((element) => {
          element.textContent = decode(value);
        });
      };

      setText('.cart-drawer__message', 'Snabb och trygg leverans!');
      // DW-SHIP-20261029: visa samma fraktregel som bannern/kassan (fri frakt från 399 kr, annars 49 kr)
      const dwThreshold = 39900;
      const dwTotal = Number(cartTotal);
      if (Number.isFinite(dwTotal)) {
        const dwRemaining = Math.max(0, dwThreshold - dwTotal);
        const dwMsg = dwRemaining === 0
          ? 'Fri frakt \u00b7 Skickas inom 48 h \u00b7 Leverans 5\u20137 dagar'
          : 'Frakt 49 kr \u00b7 L\u00e4gg till ' + money(dwRemaining) + ' till f\u00f6r fri frakt';
        this.querySelectorAll('.free-shipping-goal__label--success').forEach((element) => { element.textContent = dwMsg; });
        this.querySelectorAll('.free-shipping-goal').forEach((goal) => { goal.classList.toggle('is-below', dwRemaining > 0); });
      }
      const shippingDefault = this.querySelector('.free-shipping-goal__label--default');
      const amount = this.querySelector('[data-left-to-spend]')?.textContent || '';
      if (shippingDefault) shippingDefault.innerHTML = decode('Handla f&#246;r ') + '<strong data-left-to-spend>' + amount + '</strong>' + decode(' till f&#246;r att f&#229; fri frakt!');
      setText('.tax-note', 'Skatt ing&#229;r. Frakt och rabatter ber&auml;knas i n&auml;sta steg.');
      setText('.cart-trust-badges p', '30 dagars returr&auml;tt p&aring; oanv&auml;nda tryck &#183; S&auml;ker betalning');
      this.querySelector('.drawer__close-btn')?.setAttribute('aria-label', decode('St&auml;ng'));

      this.querySelectorAll('.cart-item').forEach((item) => {
        const title = item.querySelector('.cart-item__title')?.textContent?.trim() || '';
        item.querySelector('.cart-item__remove')?.setAttribute('aria-label', decode('Ta bort ') + title);
        item.querySelector('[name="minus"]')?.setAttribute('aria-label', 'Minska antal');
        item.querySelector('[name="plus"]')?.setAttribute('aria-label', decode('&Ouml;ka antal'));
        item.querySelector('.quantity__input')?.setAttribute('aria-label', decode('Antal f&#246;r ') + title);
        item.querySelectorAll('.cart-item__option-value strong').forEach((label) => {
          const key = label.textContent.trim().replace(/:$/, '');
          const names = { Size: 'Storlek', 'Premium Frame': 'Premiumram', Frame: 'Ram', 'Hanging kit': 'Upph&auml;ngningskit' };
          if (names[key]) label.textContent = decode(names[key]) + ':';
        });
        item.querySelectorAll('.cart-item__option-value').forEach((row) => {
          const values = { Gold: 'Guld', Black: 'Svart', White: 'Vit', 'No frame': 'Ingen ram' };
          const valueNode = Array.from(row.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
          const rawValue = valueNode?.textContent.trim();
          if (valueNode && values[rawValue]) valueNode.textContent = ' ' + decode(values[rawValue]);
        });
      });

    }

    renderShipping(subtotal) {
      const goal = this.querySelector('free-shipping-goal');
      if (!goal) return;

      const threshold = Number(goal.dataset.minimumAmount || 499) * 100;
      const progress = threshold ? Math.min(1, Math.max(0, Number(subtotal || 0) / threshold)) : 1;
      const remaining = Math.max(0, threshold - Number(subtotal || 0));
      const progressBar = goal.querySelector('progress-bar');
      const indicator = goal.querySelector('[data-source-cart-shipping-indicator]');

      goal.style.setProperty('--source-cart-shipping-progress', String(progress));
      goal.dataset.cartTotal = String(subtotal || 0);
      goal.classList.toggle('is-reached', remaining === 0);
      goal.querySelector('[data-left-to-spend]')?.replaceChildren(document.createTextNode(money(remaining)));
      if (progressBar) {
        progressBar.dataset.value = String(Number(subtotal || 0) / 100);
        progressBar.dataset.max = String(threshold / 100);
        progressBar.style.setProperty('--percent', `${progress * 100}%`);
      }
      if (indicator) indicator.style.width = `${progress * 100}%`;
    }

    renderPromotion() {
      // Mängdrabatt-steget är borttaget: butiken har ingen automatisk volymrabatt konfigurerad,
      // så ingen rabattstege får visas i varukorgen.
      this.querySelectorAll('[data-source-cart-promotion]').forEach((promotion) => promotion.remove());
    }

    renderDiscounts(discounts) {
      const footer = this.querySelector('[data-source-cart-footer] .drawer__footer-body');
      if (!footer) return;

      let list = footer.querySelector('[data-source-cart-discounts]');
      if (!discounts.length) {
        list?.remove();
        return;
      }

      if (!list) {
        list = document.createElement('div');
        list.className = 'source-cart-discounts';
        list.dataset.sourceCartDiscounts = '';
        footer.prepend(list);
      }

      list.innerHTML = discounts.map((discount) => {
        const title = escapeHtml(discount.title || 'Rabatt');
        const amount = money(discount.total_allocated_amount || 0);
        return '<span>' + title + '</span><strong>-' + amount + '</strong>';
      }).join('');
    }

    itemMarkup(item, line) {
      const options = (item.options_with_values || [])
        .filter((option) => option.value && option.value !== 'Default Title')
        .map((option) => `<div class="cart-item__option-value text-sm text-subtext"><strong>${escapeHtml(option.name)}:</strong> ${escapeHtml(option.value)}</div>`)
        .join('');
      const properties = Object.entries(item.properties || {})
        .filter(([name, value]) => value && !name.startsWith('_'))
        .map(([name, value]) => `<div class="cart-item__option-value text-sm text-subtext"><strong>${escapeHtml(name)}:</strong> ${escapeHtml(value)}</div>`)
        .join('');
      const image = item.image
        ? `<img src="${escapeHtml(imageUrl(item.image))}" alt="${escapeHtml(item.product_title)}" width="200" height="200" loading="lazy">`
        : '';
      const discounts = (item.line_level_discount_allocations || []).map((discount) => {
        const title = discount.discount_application?.title || discount.title || 'Rabatt';
        return '<li class="cart-item__discount"><svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none"><path d="M2.75 8.1a.75.75 0 0 1-.22-.53V2.75h4.82c.2 0 .39.08.53.22l5.15 5.15a.75.75 0 0 1 0 1.06l-3.85 3.85a.75.75 0 0 1-1.06 0L2.75 8.1Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="5.25" cy="5.25" r=".8" fill="currentColor"/></svg><span>' + escapeHtml(title) + '</span></li>';
      }).join('');
      const isOnSale = Number(item.original_line_price) > Number(item.final_line_price);
      const pricing = '<div class="price' + (isOnSale ? ' price--on-sale' : '') + '">' +
        (isOnSale ? '<s>' + money(item.original_line_price) + '</s>' : '') +
        '<span class="price__regular">' + money(item.final_line_price) + '</span></div>';

      return `<li class="cart-item flex flex-col" data-source-cart-line="${line}">
        <div class="cart-item__product flex items-start gap-3">
          <a class="cart-item__media blocks-radius media-wrapper" href="${escapeHtml(item.url)}" tabindex="-1">${image}</a>
          <div class="cart-item__details flex-grow flex flex-col gap-3">
            <div class="flex justify-between gap-3">
              <div class="grid flex-1">
                <div class="block"><a href="${escapeHtml(item.url)}" class="cart-item__title text-pcard-title reversed-link">${escapeHtml(item.product_title)}</a></div>
                <div class="cart-item__options">${options}${properties}</div>
              </div>
              <button type="button" class="cart-item__remove flex items-center justify-center relative btn-remove" data-source-cart-remove="${line}" aria-label="${escapeHtml(item.product_title)} entfernen"><svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24"><path d="M18.75 5.13 5.25 18.85M18.75 18.85 5.25 5.13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>
            </div>
            <div class="cart-item__action flex items-center justify-between gap-3">
              <quantity-input class="cart-quantity quantity self-end">
                <button type="button" name="minus" class="quantity__button" aria-label="Menge verringern"><svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none"><path d="M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>
                <input class="quantity__input" type="number" value="${item.quantity}" min="0" step="1" data-source-cart-quantity="${line}" aria-label="Menge für ${escapeHtml(item.product_title)}">
                <button type="button" name="plus" class="quantity__button" aria-label="Menge erhöhen"><svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M8 3v10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>
              </quantity-input>
              <div class="cart-item__prices text-right flex flex-col gap-2">${discounts ? '<ul class="cart-item__discounts flex flex-wrap" role="list" aria-label="Rabatt">' + discounts + '</ul>' : ''}${pricing}</div>
            </div>
          </div>
        </div>
      </li>`;
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', SourceCartDrawer);
})();
